<template>
  <div
    class="block-form-container"
    :class="{ 'create-mode': isCreateMode }"
  >
    <div class="form-header-row">
      <h5 class="form-title">
        {{ isCreateMode ? "Создание блока" : "Редактирование блока" }}
      </h5>
    </div>

    <!-- Настройки -->
    <div class="meta-grid">
      <div class="form-group">
        <base-label :required="true">Название</base-label>
        <base-input
          v-model="form.name"
          placeholder="Название блока"
        />
      </div>
      <div class="form-group">
        <base-label :required="true">Тип блока</base-label>
        <BaseSelect
          v-model="form.field_type"
          :options="blockTypeOptions"
          @change="onTypeChange"
          :clearable="false"
        />
      </div>

      <!-- Панель данных -->
      <div class="data-source-group">
        <base-label>Данные для теста</base-label>
        <div class="ds-controls">
          <BaseSelect
            v-model="attrForm.record_type"
            :options="recordTypeOptions"
            class="ds-type"
            :clearable="false"
          />
          <OrderSelect
            v-model="formData.order_id"
            placeholder="Выберите заказ"
            class="ds-order-select"
            :clearable="false"
            :auto-select-first="true"
          />
        </div>
      </div>
    </div>

    <div class="workspace-grid">
      <!-- Визуальный редактор -->
      <div class="editor-col">
        <TemplateBlockVisualEditor
          v-if="form.field_type"
          ref="visualEditor"
          v-model="configObject"
          :block-type="form.field_type"
          :template-styles="templateGlobalStyles"
        />

        <div v-else class="empty-editor-msg">
          Выберите тип блока, чтобы начать настройку содержимого.
        </div>

        <div class="json-spoiler">
          <div class="spoiler-header" @click="showJson = !showJson">
            <span>
              {{ showJson ? "Скрыть JSON" : "Показать JSON" }}
            </span>
          </div>
          <div v-if="showJson" class="json-area">
            <base-textarea
              v-model="configString"
              class="raw-json-textarea"
              rows="6"
              @input="onJsonInput"
            ></base-textarea>
            <div v-if="jsonError" class="json-error">
              {{ jsonError }}
            </div>
          </div>
        </div>
      </div>

      <!-- Пикер -->
      <div class="picker-col">
        <div class="picker-wrapper">
          <TemplateAttributePicker
            v-if="hasAttributes"
            :data="attributes"
            :item-data="itemAttributes"
            :block-type="form.field_type"
            :is-loading-item-attrs="areItemAttributesLoading"
            @insert="handleInsertAttribute"
          />
          <div v-else class="empty-picker">
            <p>Выберите заказ, чтобы видеть доступные переменные</p>
          </div>
        </div>
      </div>
    </div>

    <div class="form-actions">
      <base-button class="btn-sm" @click="$emit('cancel')">
        Отмена
      </base-button>
      <base-primary-button
        class="btn-sm"
        @click="save"
        :loading="isLoading"
      >
        {{ isCreateMode ? "Создать" : "Сохранить" }}
      </base-primary-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";

import { useTemplateStore } from "@/stores/templateStore";

import TemplateAttributePicker from "@/components/Template/TemplateAttributePicker.vue";
import TemplateBlockVisualEditor from "@/components/Template/TemplateBlockVisualEditor.vue";
import OrderSelect from "@/components/Order/OrderSelect.vue";

export default {
  name: "TemplateBlockForm",
  components: {
    TemplateAttributePicker,
    TemplateBlockVisualEditor,
    OrderSelect,
  },
  props: {
    initialData: { type: Object, default: () => ({}) },
    templateId: { type: [Number, String], required: true },
    companyId: { type: [Number, String], required: true },
    isCreateMode: { type: Boolean, default: false },
    nextPosition: { type: Number, default: 1 },
    recordId: { type: Number, default: null },
    recordType: { type: String, default: "Order" },
    templateGlobalStyles: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      areAttributesLoading: false,
      areItemAttributesLoading: false,
      form: { name: "", field_type: "", position: 1 },
      formData: {
        order_id: null,
      },
      attrForm: {
        record_type: this.recordType,
        record_id: this.recordId,
      },
      recordTypeOptions: [{ value: "Order", label: "Заказ" }],
      configObject: {},
      configString: "{}",
      showJson: false,
      jsonError: null,
      ignoreUpdate: false,
    };
  },

  computed: {
    ...mapState(useTemplateStore, [
      "blockTypeOptions",
      "defaultBlockConfigs",
      "attributes",
      "itemAttributes",
    ]),
    hasAttributes() {
      return (
        this.attributes &&
        this.attributes.parsed_attrs &&
        this.attributes.parsed_attrs.length > 0
      );
    },
    hasItemAttributes() {
      return (
        this.itemAttributes &&
        this.itemAttributes.parsed_attrs &&
        this.itemAttributes.parsed_attrs.length > 0
      );
    },
    shouldLoadItemAttrs() {
      return ["items_table", "sectioned_items_table"].includes(
        this.form.field_type
      );
    },
    currentDataSource() {
      if (!this.shouldLoadItemAttrs) return null;
      return this.configObject.data_source || "trip_vehicles";
    },
  },

  watch: {
    configObject: {
      handler(newVal) {
        if (this.ignoreUpdate) return;
        this.configString = JSON.stringify(newVal, null, 2);

        if (this.shouldLoadItemAttrs && newVal.data_source) {
          this.loadItemAttributes();
        }
      },
      deep: true,
    },
    "formData.order_id": {
      handler(newOrderId) {
        if (newOrderId) {
          this.attrForm.record_id = newOrderId;
          this.loadAttributes();

          if (this.shouldLoadItemAttrs) {
            this.loadItemAttributes();
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions(useTemplateStore, [
      "createBlock",
      "updateBlock",
      "renderTemplateAttributes",
      "renderTemplateItemAttributes",
    ]),

    async loadItemAttributes() {
      if (!this.attrForm.record_id || !this.currentDataSource) return;

      const recordTypeMap = {
        trip_vehicles: "TripVehicle",
        trip_drivers: "TripDriver",
        order_points: "OrderPoint",
      };

      const itemRecordType = recordTypeMap[this.currentDataSource];
      if (!itemRecordType) return;

      this.areItemAttributesLoading = true;
      try {
        await this.renderTemplateItemAttributes({
          record_type: itemRecordType,
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.areItemAttributesLoading = false;
      }
    },

    onJsonInput() {
      try {
        const obj = JSON.parse(this.configString);
        this.jsonError = null;
        this.ignoreUpdate = true;
        this.configObject = obj;
        this.$nextTick(() => {
          this.ignoreUpdate = false;
        });
      } catch (e) {
        this.jsonError = e.message;
      }
    },

    async loadAttributes() {
      if (!this.attrForm.record_id) return;
      this.areAttributesLoading = true;
      try {
        await this.renderTemplateAttributes({
          record_id: this.attrForm.record_id,
          record_type: this.attrForm.record_type,
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.areAttributesLoading = false;
      }
    },

    handleInsertAttribute(textToInsert) {
      if (this.$refs.visualEditor)
        this.$refs.visualEditor.insertVariable(textToInsert);
    },

    onTypeChange() {
      if (
        this.isCreateMode ||
        !this.configString ||
        this.configString === "{}"
      ) {
        const def = this.defaultBlockConfigs[this.form.field_type];
        this.configObject = def
          ? JSON.parse(JSON.stringify(def))
          : {};
      }
    },

    async save() {
      if (!this.form.name) return alert("Введите название блока");

      this.isLoading = true;

      const finalConfig = { ...this.configObject };

      if (
        this.form.field_type === "items_table" &&
        finalConfig.rows
      ) {
        if (
          Array.isArray(finalConfig.rows) &&
          finalConfig.rows.length === 1 &&
          Array.isArray(finalConfig.rows[0])
        ) {
          finalConfig.rows = finalConfig.rows[0];
        }
      }

      const payload = {
        ...this.form,
        config: finalConfig,
      };

      try {
        if (this.isCreateMode)
          await this.createBlock(this.templateId, payload);
        else
          await this.updateBlock(
            this.templateId,
            this.form.id,
            payload
          );
        this.$emit("save");
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    initData() {
      if (!this.isCreateMode && this.initialData) {
        const { config, ...rest } = this.initialData;
        this.form = { ...rest };

        if (typeof config === "string") {
          try {
            this.configObject = JSON.parse(config);
          } catch {
            this.configObject = {};
          }
        } else {
          this.configObject = config || {};
        }

        if (
          this.form.field_type === "items_table" &&
          this.configObject.rows &&
          Array.isArray(this.configObject.rows) &&
          this.configObject.rows.length > 0 &&
          typeof this.configObject.rows[0] === "string"
        ) {
          this.configObject.rows = [this.configObject.rows];
        }
      } else {
        this.form.position = this.nextPosition;
        this.configObject = {};
      }
    },
  },
  mounted() {
    this.initData();
  },
};
</script>

<style scoped>
.block-form-container {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
}

.create-mode {
  border: 2px dashed #d1d5db;
  background-color: #f9fafb;
}

.form-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.form-header-row {
  margin-bottom: var(--base-in-indent);
}

.meta-grid {
  display: flex;
  flex-direction: column;
  margin-bottom: var(--base-out-indent);
}

.ds-controls {
  display: flex;
  gap: 5px;
  align-items: center;
}

.ds-type {
  width: 100%;
  min-width: 120px;
}

.ds-order-select {
  width: 200px;
  min-width: 200px;
}

.workspace-grid {
  display: grid;
  grid-template-columns: 3fr 1.2fr;
  gap: 20px;
  align-items: start;
}

.editor-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.picker-col {
  position: sticky;
  top: 10px;
  min-width: 0;
}

.picker-wrapper {
  height: 700px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.empty-picker {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
  padding: 10px;
}

.empty-editor-msg {
  text-align: center;
  padding: 30px;
  border: 1px dashed #ccc;
  background: #fafafa;
  color: #888;
  border-radius: 6px;
}

.json-spoiler {
  margin-top: 10px;
}

.spoiler-header {
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
  text-decoration: underline;
}

.raw-json-textarea {
  width: 100%;
  margin-top: 5px;
  font-family: monospace !important;
  font-size: 12px !important;
  border: 1px solid #ddd !important;
  background: #fafafa !important;
}

.json-error {
  color: red;
  font-size: 11px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: var(--base-out-indent);
}

.btn-sm {
  padding: 6px 14px;
  font-size: 13px;
}

@media (max-width: 1024px) {
  .meta-grid {
    grid-template-columns: 1fr 1fr;
  }

  .data-source-group {
    grid-column: span 2;
  }

  .workspace-grid {
    grid-template-columns: 1fr;
  }

  .picker-wrapper {
    height: 300px;
  }

  .picker-col {
    position: static;
  }
}

@media (max-width: 600px) {
  .meta-grid {
    grid-template-columns: 1fr;
  }

  .data-source-group {
    grid-column: span 1;
  }

  .ds-controls {
    flex-wrap: wrap;
  }

  .ds-order-select {
    width: 100%;
    min-width: 100%;
  }
}
</style>
